define([
	'jquery',
	'BaseView',
	'backbone',
	'underscore',
	'widgets/alertMsg',
	'widgets/clearInput'
], function ($, BaseView, Backbone, _, AlertMsg, ClearInput) {

	'use strict';

	/**
	 * Notes:
	 * 1) Input type 'number' doesn't support maxlength and it is conflicting with lap in Chrome.
	 * 2) Mobile shows non-number keyboard for number pattern with length range, ex. '[0-9]{14,16}'.
	 * The work around is to use 'text' input type, '[0-9]*' pattern, and custom 'data-min' attributes for validation.
	 *
	 * Usage:
	 * // initialize validatForm view
	 * addCreditformView = new ValidateForm({
	 *     el: 'form[name='addCreditOrDebit']'
	 * });
	 *
	 * // set min length (optional) for input fields
	 * $cscInputField.data('min', '3');
	 * $ccnInputField.data('min', '14');
	 *
	 * // assign class name 'validateBeforeSubmit' for the button to be interacted with
	 * <a class='validateBeforeSubmit' href='#'>Continue</a>
	 * or
	 * <input class='validateBeforeSubmit' type='submit' value='Continue' />
	 */

	var View = BaseView.extend({
		timer: null,
		customValidate: null,
		customRule: null,
		next: null,
		submitBtn: null,
		checkInput: null,
		preValue: null,
		timeouts: [],

		events: {
			'change select[required]:visible': 'validating',
			'focus input[required]:visible': 'startValidating',
			'focus .js_needsValidation:visible': 'startValidating',
			'focusout input[required]:visible': 'stopValidating',
			'focusout .js_needsValidation:visible': 'stopValidating',
			'click .validateBeforeSubmit': 'validateBeforeSubmit'
		},

		initialize: function (params) {
			params = params || {};

			// custom call back function (optional)
			this.next = params.next;
			this.alertMsg = new AlertMsg();

			// custom function to loop through inputs and apply validate rule to each (optional)
			this.customValidate = params.customValidate;
			// custom rule to validate fields (optional)
			this.customRule = params.customRule;

			// trigger inputValid event when this input field (checkInput) is valid (optional)
			// using by add bank flow to retrieve bank name when routing number is valid
			this.checkInput = params.checkInput;

			// initialize the clear input widget
			this.clearInput = new ClearInput({el: this.$el});

			this.guardMultiSubmit = params.guardMultiSubmit || false;
		},

		/**
		 * Function used in polling form fields to check validity
		 *
		 * @function validating
		 * @param  {Boolean} showError
		 */
		validating: function (showError) {
			var valid = this.validateFields(
					$('input[required]:visible, .js_needsValidation:visible'),
					this.$('select[required]:visible'), showError),
				$checkInput = $(this.checkInput);

			if (this.next) {
				this.next(valid);
			}

			if (this.checkInput && this.preValue !== $checkInput.val()) {
				this.preValue = $checkInput.val();
				// remove any errors when trying to validate input again
				$checkInput.parent().removeClass('hasError');
				$checkInput.parent().removeClass('vx_has-error');
				if (this.validateFields(this.checkInput)) {
					Backbone.trigger('inputValid');
				}
			}
		},

		/**
		 * When class `.validateBeforeSubmit` is added to a form submit button, validations will run; the function blocks
		 * submission if conditions are not met, or allows submission to proceed if all is well.
		 *
		 * @function validateBeforeSubmit
		 * @param {Object} event
		 * @returns {Boolean}
		 */
		validateBeforeSubmit: function (event) {
			var $btn,
				$inputs = this.$('textarea[required]:visible, input[required]:visible, .js_needsValidation:visible'),
				$selects = this.$('select[required]:visible'),

			// get the valid status using validateFields
				valid = this.validateFields($inputs, $selects);

			if (!valid) {
				if (event) {
					event.preventDefault();
					event.stopPropagation();
				}
				setTimeout($.proxy(function () {
					// show errors using validateFields with beforeSubmit and showError true
					this.validateFields($inputs, $selects, true, true);
					this.trackErrors($inputs, $selects);
				}, this), 100);
				return false;
			}

			if (this.guardMultiSubmit) {
				$btn = this.$(event.currentTarget);
				$btn.attr('disabled', 'disabled');
				this.$el.submit();
				// No subsequent actions are needed because submit here.
				return false;
			}

			return true;
		},

		/**
		 * Logs client-side errors to FPTI and SiteCatalyst (triggers trackError in analytics.js)
		 *
		 * @function trackErrors
		 * @param {Array} $inputs
		 * @param {Array} $selects
		 */
		trackErrors: function ($inputs, $selects) {
			var $erroredFields = $inputs.add($selects),
				$errorEl;

			$erroredFields.each($.proxy(function (i, el) {
				$errorEl = $(el).parent('.hasError');
				if ($errorEl && $errorEl.length > 0) {
					Backbone.trigger('trackError', {
						message: $(el).siblings('.help-error').text(),
						fieldId: el.id
					});
				}
			}, this));
		},

		/**
		 * Polls `this.validating` by adding a recursive calls to an array, `this.timeouts`, while validation is active
		 *
		 * @function validateTimer
		 */
		validateTimer: function () {
			this.validating();
			this.timeouts.push(setTimeout($.proxy(function () {
				this.validateTimer();
			}, this), 300));
		},

		/**
		 * Calls `this.validateTimer` to start validation polling
		 *
		 * @function startValidating
		 */
		startValidating: function () {
			this.validateTimer();
		},

		/**
		 * Calls `this.validating` one last time with flag to display error for invalid input and clears out `this.timeouts`
		 *
		 * @function stopValidating
		 */
		stopValidating: function () {
			var $textInput = $(this.checkInput).parent(),
				i;

			this.trimInput();

			setTimeout($.proxy(function () {
				this.validating(true);
			}, this), 50);

			// if the input still has an error, keep the hasError class on it
			if ($textInput.hasClass('hasError')) {
				setTimeout(function () {
					$textInput.addClass('hasError');
				}, 0);
			}

			for (i = 0; i < this.timeouts.length; i++) {
				clearTimeout(this.timeouts[i]);
			}
		},

		/**
		 * validate form fields
		 *
		 * FYI, the validateForm instance in
		 * pub/js/view/activity/filterView has its own override of this
		 *
		 * @function validateFields
		 * @param {Array} $inputs
		 * @param {Array} $selects
		 * @param {Boolean} showError
		 * @param {Boolean} beforeSubmit
		 * @returns {Boolean} valid
		 */
		validateFields: function ($inputs, $selects, showError, beforeSubmit) {
			var valid = true,
				isDisabled;

			if (this.customValidate) {
				// make overridable
				return this.customValidate($inputs, $selects, showError, beforeSubmit);
			}

			if ($selects) {
				$selects.each(function () {
					$(this).parent().parent().removeClass('hasError');
					$(this).parent().removeClass('vx_has-error');
					$(this).parent().children('.vx_selectIcon_error').removeClass('vx_has-error');
					if ($(this).val() === '-1') {
						valid = false;
						if (beforeSubmit) {
							$(this).parent().parent().addClass('hasError');
						}
					}
					if ($(this).val() === '-11') {
						valid = false;
						isDisabled = $(this).prop('disabled');
						if (beforeSubmit && !isDisabled) {
							$(this).parent().addClass('vx_has-error');
							$(this).parent().children('.vx_selectIcon_error').addClass('vx_has-error');
						}
					}

				});
			}

			// Spin through each input to check if the input is valid or not.
			$inputs.each(_.bind(function (inputIndex, inputElement) {
				var $currentInput = $(inputElement),
					$currentInputParent = $currentInput.parent(),
					$errorMessageTooltip = $currentInputParent.find('.help-error'),
					trimmedVal;

				if (!$currentInput.val() || ($currentInput.is(':checkbox') && !$currentInput.attr('checked'))) {
					$currentInputParent.removeClass('hasError');

					// only explicitly modify `valid` if empty input is required
					if (this.checkInputRequired($currentInput)) {
						valid = false;
						if (beforeSubmit) {
							this.initInputErrorState($currentInputParent);
						}
					}
					/**
					 * 3 conditions if bank name text uses typeahead
					 * 1. Check if input uses typeahead
					 * 2. Ensure user clicks into the typeahead and selects an entry, right now this is
					 * enforced by ensuring that the hidden field is NOT having null value or having value=others
					 * 3. For CH, bankname gets passed as is, to ensure there is minimal validation , we make sure
					 * that the user does NOT focus out of the bank name field while the typeahead dropdown
					 * still has values. This is because handling bank name for CH is different from that of BR
					 */
				} else if ($currentInput.hasClass('js_usesTypeAhead') && $('.input-typeahead') &&
					(!($('.input-typeahead').has('li').length) ||
					(this.$('.js_hiddenBankName').length && (!this.$('.js_hiddenBankName').val() || this.$('.js_hiddenBankName').val() === 'others')))) {
					valid = false;
					if (beforeSubmit || showError) {
						this.initInputErrorState($currentInputParent);
					}
				} else if ($currentInputParent.hasClass('hasError')) {
					valid = this.checkInputValidation($currentInput, $.trim($currentInput.val()));
				} else {

					// trim spaces
					// note: use $.trim() for ie8
					trimmedVal = $.trim($currentInput.val());

					if (!this.checkInputValidation($currentInput, trimmedVal)) {
						valid = false;
						if (!beforeSubmit) {
							if (showError && trimmedVal !== '') {
								this.initInputErrorState($currentInputParent, $errorMessageTooltip);
							}
						}
					}
				}

				// If the input is still in a valid state, then remove error messaging.
				if (valid) {
					this.removeInputErrorState($currentInputParent, $errorMessageTooltip);
				}
			}, this));

			// validate using custom rule function that returns boolean valid
			if (valid && this.customRule) {
				valid = this.customRule(valid);
			}

			return valid;
		},

		/**
		 * Check Input against all validation checks
		 *
		 * @function checkInputValidation
		 * @param {Object}  $currentInput - The Input we are checking.
		 * @param {String}  trimmedVal - Chomp'd Input
		 * @returns {Boolean}
		 */
		checkInputValidation: function($currentInput, trimmedVal) {

			// This is a hard override that skips all validation.
			if (this.checkSkipValidation($currentInput)) {
				return true;
			}

			// If any of these fail, the input is invalid
			if (!(this.checkInputPattern($currentInput, trimmedVal) &&
				this.checkInputDataMin($currentInput, trimmedVal) &&
				this.checkInputMin($currentInput, trimmedVal) &&
				this.checkInputMax($currentInput, trimmedVal) &&
				this.checkInputMaxLength($currentInput, trimmedVal))) {

				return false;
			}

			// All the validation checks passed, so the input is marked as valid
			return true;
		},

		/**
		 * Skip validation if the skip-validate data attribute is set to true
		 *
		 * @function checkSkipValidation
		 * @param {Object} $currentInput - The Input we are checking.
		 * @returns {Boolean}
		 */
		checkSkipValidation: function ($currentInput) {
			return $currentInput.data('skip-validate') === true;
		},

		/**
		 * Determines if the input is required or not
		 *
		 * @function checkInputRequired
		 * @param {Object} $currentInput - The Input we are checking.
		 * @returns {Boolean}
		 */
		checkInputRequired: function ($currentInput) {
			return $currentInput.prop('required') === true;
		},

		/**
		 * Just add the hasError class to the input's parent, but don't open the error bubble
		 *
		 * @function initInputErrorState
		 * @param {Object} $inputParent - Input Wrapper that we want to point out having an error
		 * @param {Object} $inputErrorToolTip - Error element related to input
		 */
		initInputErrorState: function ($inputParent, $inputErrorToolTip) {
			$inputParent.addClass('hasError');

			if ($inputErrorToolTip) {
				$inputErrorToolTip.addClass('open');
			}
		},

		/**
		 * Updates UI to remove error for input container + remove any error tooltip
		 *
		 * @function removeInputErrorState
		 * @param {Object} $inputParent - Input Wrapper that we want to point out having an error
		 * @param {Object} $inputErrorToolTip - Tooltip that we want to 'close'
		 */
		removeInputErrorState: function ($inputParent, $inputErrorToolTip) {
			$inputParent.removeClass('hasError');
			$inputErrorToolTip.removeClass('open');
		},

		/**
		 * Updates UI to remove error for input container + remove any help tooltip
		 *
		 * @function removeInputError
		 * @param {Object} $input - jQuery object for input to update
		 */
		removeInputError: function ($input) {
			this.removeInputErrorState($input.parent(), $input.parent().children('.help-error'));
			$input.attr('aria-invalid', 'false'); // it falsely is invalid
		},

		/**
		 * Updates UI to show error for input container + reveal help tooltip
		 *
		 * @function initInputError
		 * @param {Object} $input - jQuery object for input to update
		 */
		initInputError: function ($input, subclass) {
			if (subclass) {
				this.removeInputErrorState($input.parent(), $input.parent().children('.help-error'));
				this.initInputErrorState($input.parent(), $input.parent().children('.help-error.' + subclass));
			} else {
				this.initInputErrorState($input.parent(), $input.parent().children('.help-error'));
			}
			$input.attr('aria-invalid', 'true'); // it truly is invalid
		},


		/**
		 * Check Input's regex Pattern
		 *
		 * @function checkInputPattern
		 * @param {Object} $currentInput - The Input we are checking.
		 * @param {String} val - Chomp'd Input
		 */
		checkInputPattern: function ($currentInput, val) {
			if ($currentInput.attr('pattern')) {
				// Continuously creating new RegExp is a bad idea.
				var pattern = new RegExp('^' + $currentInput.attr('pattern') + '$');
				if (!pattern.test(val)) {
					return false;
				}
			}
			return true;
		},

		/**
		 * Check Input's data-min attribute
		 *
		 * @function initInputErrorState
		 * @param {Object} $currentInput - The Input we are checking.
		 * @param {String} val - Chomp'd Input
		 */
		checkInputDataMin: function ($currentInput, val) {
			if ($currentInput.data('min')) {
				if (val.length < $currentInput.data('min')) {
					return false;
				}
			}
			return true;
		},

		/**
		 * Assemble regex validation pattern based on supplied separator or separator found on input
		 *
		 * @function getRegExp
		 * @param {Object} $currentInput
		 * @param {String} separator
		 * @returns {{decimalSeparator: (*|string), regexPattern: RegExp}}
		 */
		getRegExp: function ($currentInput, separator) {
			var decimalSeparator = separator || $currentInput.data('decimal-separator') || '.',
				regex = {
					decimalSeparator: decimalSeparator,
					regexPattern: new RegExp('[^0-9\\' + decimalSeparator + ']', 'g')
				};
			return regex;
		},

		/**
		 * Get float value of input amount
		 *
		 * @function getRawValue
		 * @param {String} val
		 * @param {String} regex
		 * @returns {Number}
		 */
		getRawValue: function (val, regex) {
			return parseFloat(val.replace(regex.regexPattern, '').replace(regex.decimalSeparator, '.'));
		},

		/**
		 * Check Input's min attribute (only applies to type='number')
		 *
		 * @function checkInputMin
		 * @param {Object} $currentInput - The Input we are checking.
		 * @param {String} val - Chomp'd Input
		 */
		checkInputMin: function ($currentInput, val, separator) {
			if ($currentInput.attr('min')) {
				var regex = this.getRegExp($currentInput, separator),
					inputValue = this.getRawValue(val, regex),
					minValue = this.getRawValue($currentInput.attr('min'), regex);
				if (parseFloat(inputValue) < parseFloat(minValue)) {
					return false;
				}
			}
			return true;
		},

		/**
		 * Check Input's max attribute (only applies to type='number')
		 *
		 * @function checkInputMax
		 * @param {Object} $currentInput - The Input we are checking.
		 * @param {String} val - Chomp'd Input
		 * @param {String} separator - Amount separator
		 */
		checkInputMax: function ($currentInput, val, separator) {
			if ($currentInput.attr('max')) {
				var regex = this.getRegExp($currentInput, separator),
					inputValue = this.getRawValue(val, regex),
					maxValue = this.getRawValue($currentInput.attr('max'), regex);
				if (inputValue > maxValue) {
					return false;
				}
			}
			return true;
		},

		/**
		 * Check Input's maxlength attribute (only applies to type='number')
		 *
		 * @function checkInputMaxLength
		 * @param {Object} $currentInput - The Input we are checking.
		 * @param {String} val - Chomp'd Input
		 */
		checkInputMaxLength: function ($currentInput, val) {
			if ($currentInput.attr('maxlength')) {
				if (val.length > $currentInput.attr('maxlength')) {
					return false;
				}
			}
			return true;
		},

		/**
		 * Trim leading & trailing white space of input value
		 *
		 * @function trimInput
		 */
		trimInput: function () {
			var $inputs = this.$('input:visible');
			$inputs.each(function () {
				var $currentInput = $(this);
				$currentInput.val($.trim($currentInput.val()));
			});
		},

		/**
		 * Add error message to alert component
		 *
		 * @function showError
		 * @param {String} msg
		 */
		showError: function (msg) {
			// remove any old messages
			this.$('.js_alertMsg').remove();
			// add a new one
			this.$el.prepend(this.alertMsg.setMessage(msg).el);
		}
	});

	return View;
});



//////////////////
// WEBPACK FOOTER
// ./widgets/validateForm.js
// module id = 47
// module chunks = 0